/**
 * URL-based rules for the A3C embedded chat under A3C_EVERYWHERE.
 *
 * Two rule sets, evaluated against the full page URL (window.location.href):
 * - EMBEDDED_MODE_URL_PATTERNS: pages that must keep the legacy embedded
 *   (content-squeeze) panel behavior even when A3C_EVERYWHERE defaults the
 *   panel to flyover mode. The chat still loads with the everywhere agent.
 * - CHAT_DISABLED_URL_PATTERNS: pages where the chat must not load at all —
 *   no container is created and no default agent is injected.
 *
 * A page-provided `embeddedChatConfig` (with a selectedAgentIdentifier)
 * always takes precedence over these URL rules shaping the flyover default.
 *
 * Prefer anchoring patterns on the pathname segment so they are stable
 * across domains and stages.
 */
const EMBEDDED_MODE_URL_PATTERNS: RegExp[] = [
  // Campaign manager (/cm, /campaign-manager) and campaign builder (/cb)
  // console pages keep the legacy embedded (content-squeeze) panel: they get
  // the default embedded config and open by default, but flyover mode never
  // applies. Matches the first pathname segment only (/cm, /cm/campaigns) —
  // not lookalikes (/cmx).
  /^[^:/?#]+:\/\/[^/?#]+\/(cm|cb|campaign-manager)([/?#]|$)/,
  // Sponsored TV campaign creation settings gets the same embedded setup as
  // the campaign builders (see isUcbEverywherePath).
  /^[^:/?#]+:\/\/[^/?#]+\/create\/settings\/st([/?#]|$)/,
];

const CHAT_DISABLED_URL_PATTERNS: RegExp[] = [];

/**
 * Returns true when the given URL must default to embedded (content-squeeze)
 * panel behavior instead of the A3C_EVERYWHERE flyover default.
 */
export function isEmbeddedModeUrl(url: string): boolean {
  return EMBEDDED_MODE_URL_PATTERNS.some((pattern) => pattern.test(url));
}

/**
 * Returns true when the chat must not load on the given URL.
 */
export function isChatDisabledUrl(url: string): boolean {
  return CHAT_DISABLED_URL_PATTERNS.some((pattern) => pattern.test(url));
}

/**
 * Paths where the A3C everywhere experience — and the Focus-header chat
 * toggle — is controlled by the dedicated A3C_EVERYWHERE_UCB_1459740 weblab
 * instead of A3C_EVERYWHERE_1451089, so UCB dials up on its own schedule:
 * - the campaign builders: /cb and any /cb/* sub-path (/cb/sp, /cb/sd,
 *   /cb/sb, /cb/sp/presets)
 * - Sponsored TV campaign creation settings: /create/settings/st and
 *   sub-paths
 * Evaluated against window.location.pathname.
 */
export function isUcbEverywherePath(pathname: string): boolean {
  return (
    pathname === '/cb' ||
    pathname.startsWith('/cb/') ||
    pathname === '/create/settings/st' ||
    pathname.startsWith('/create/settings/st/')
  );
}

/**
 * Paths where the A3C everywhere experience is controlled by the dedicated
 * A3C_EVERYWHERE_UCM_1461968 weblab instead of A3C_EVERYWHERE_1451089, so
 * the Unified Campaign Manager (UCM) dials up on its own schedule:
 * - the campaign manager: /cm and any /cm/* sub-path (/cm/sp, /cm/campaigns)
 * - the unified landing pages: /campaign-manager and sub-paths
 *   (/campaign-manager/all-campaigns)
 * Evaluated against window.location.pathname.
 */
export function isUcmEverywherePath(pathname: string): boolean {
  return (
    pathname === '/cm' ||
    pathname.startsWith('/cm/') ||
    pathname === '/campaign-manager' ||
    pathname.startsWith('/campaign-manager/')
  );
}
